import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { Lock, Mail, ArrowRight, ShieldCheck, ChevronLeft, CheckCircle2, Zap, LineChart } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { user, loading, signInWithPassword, resetPasswordForEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      if (showForgot) {
        await resetPasswordForEmail(email);
        setSuccess('Password reset link has been sent to your email.');
      } else {
        await signInWithPassword({ email, password });
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-yellow-50">
      
      {/* Subtle Background Elements */}
      <div className="fixed top-0 left-1/4 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none mix-blend-multiply"></div>
      <div className="fixed bottom-0 right-1/4 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-[150px] pointer-events-none mix-blend-multiply"></div>

      {/* Floating Feature Elements (Background) */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        {/* Top Left Feature */}
        <div className="absolute top-20 left-[10%] md:left-[20%] bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-200/50 shadow-sm transform -rotate-6 animate-pulse" style={{ animationDuration: '4s' }}>
          <div className="flex items-center gap-3 opacity-70">
            <div className="h-10 w-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Zap className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-800">Smart Validation</div>
              <div className="text-[10px] text-slate-500">Error-free AWB entries</div>
            </div>
          </div>
        </div>

        {/* Bottom Left Feature */}
        <div className="absolute bottom-32 left-[5%] md:left-[15%] bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-200/50 shadow-sm transform rotate-12 animate-pulse" style={{ animationDuration: '6s', animationDelay: '1s' }}>
          <div className="flex items-center gap-3 opacity-70">
            <div className="h-10 w-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-800">Auto GST Split</div>
              <div className="text-[10px] text-slate-500">Real-time calculations</div>
            </div>
          </div>
        </div>

        {/* Top Right Feature */}
        <div className="absolute top-40 right-[5%] md:right-[15%] bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-200/50 shadow-sm transform rotate-6 animate-pulse" style={{ animationDuration: '5s', animationDelay: '2s' }}>
          <div className="flex items-center gap-3 opacity-70">
            <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <LineChart className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-800">Live Reports</div>
              <div className="text-[10px] text-slate-500">Track every penny</div>
            </div>
          </div>
        </div>

        {/* Bottom Right Feature */}
        <div className="absolute bottom-20 right-[10%] md:right-[20%] bg-white/60 backdrop-blur-sm p-4 rounded-2xl border border-slate-200/50 shadow-sm transform -rotate-12 animate-pulse" style={{ animationDuration: '7s', animationDelay: '0.5s' }}>
          <div className="flex items-center gap-3 opacity-70">
            <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Mail className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-800">Instant Dispatch</div>
              <div className="text-[10px] text-slate-500">Share PDFs instantly</div>
            </div>
          </div>
        </div>

        {/* Top Center-ish Background Accent */}
        <div className="absolute top-10 left-[45%] md:left-[50%] bg-white/40 backdrop-blur-sm p-3 rounded-xl border border-slate-200/30 shadow-sm transform -rotate-2 animate-pulse scale-75" style={{ animationDuration: '4.5s', animationDelay: '1.5s' }}>
          <div className="flex items-center gap-2 opacity-50">
            <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Lock className="h-4 w-4 text-indigo-400" />
            </div>
            <div>
              <div className="text-[10px] font-bold text-slate-600">Secure Core</div>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Top Accent Line */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-indigo-600 to-orange-500"></div>

      <div className="relative z-10 w-full max-w-md px-6">
        
        {/* Logo/Brand Header */}
        <div className="flex flex-col items-center mb-8">
          <div 
              className="flex items-center cursor-pointer group mb-2" 
              onClick={() => navigate('/')}
          >
              <span className="text-3xl font-black tracking-tight text-indigo-900 transition-colors group-hover:text-indigo-700">FR-Bill</span>
              <span className="text-3xl font-black tracking-tight text-orange-500 transition-colors group-hover:text-orange-600">Net</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-px w-6 bg-slate-300"></span>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-[0.2em]">Console Access</p>
            <span className="h-px w-6 bg-slate-300"></span>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-black text-slate-900">
                {showForgot ? 'Reset Password' : 'Sign In'}
              </h2>
              {showForgot && (
                <button 
                  onClick={() => { setShowForgot(false); setError(''); setSuccess(''); }}
                  className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition-colors text-sm font-bold"
                >
                  <ChevronLeft size={16} />
                  Back
                </button>
              )}
            </div>
            
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm font-medium flex items-start gap-3">
                <div className="w-1 h-5 mt-0.5 bg-red-500 rounded-full shrink-0"></div>
                {error}
              </div>
            )}

            {success && (
              <div className="mb-6 p-6 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm flex flex-col items-center gap-3 text-center">
                <CheckCircle2 size={36} className="text-emerald-500 mb-2" />
                <p className="font-medium">{success}</p>
                <button 
                  onClick={() => { setShowForgot(false); setSuccess(''); }}
                  className="mt-4 text-white bg-indigo-600 hover:bg-indigo-700 px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest shadow-sm transition-colors"
                >
                  Return to Login
                </button>
              </div>
            )}

            {!success && (
              <form onSubmit={onSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Email Address</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                      <Mail size={18} />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="block w-full pl-11 pr-4 py-3.5 bg-yellow-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                      placeholder="name@company.com"
                    />
                  </div>
                </div>

                {!showForgot && (
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">Password</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                        <Lock size={18} />
                      </div>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="block w-full pl-11 pr-4 py-3.5 bg-yellow-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                )}

                {!showForgot && (
                  <div className="flex items-center justify-between text-sm py-2">
                    <label className="flex items-center gap-2 text-slate-600 cursor-pointer hover:text-indigo-600 transition-colors font-medium">
                      <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/30" />
                      Remember me
                    </label>
                    <button 
                      type="button" 
                      onClick={() => { setShowForgot(true); setError(''); }}
                      className="text-indigo-600 hover:text-indigo-800 font-bold transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-4 flex items-center justify-center gap-2 py-4 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm hover:shadow-md transform transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{showForgot ? 'Sending...' : 'Authenticating...'}</span>
                    </div>
                  ) : (
                    <>
                      <span>{showForgot ? 'Send Reset Link' : 'Sign In to Console'}</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Footer Sign Up Link */}
          {!showForgot && (
            <div className="bg-yellow-50 p-4 border-t border-slate-100 flex items-center justify-center text-sm">
              <p className="text-slate-500 font-medium">
                Don't have an account?{' '}
                <button onClick={() => navigate('/signup')} className="text-indigo-600 hover:text-indigo-800 font-bold ml-1">
                  Sign Up
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
